// Client-side prompt-search scoring for the events discover page.
// Pure functions — no React, no IO. Substring-match per token, weighted sum.

const STOPWORDS = new Set([
  "and", "or", "the", "a", "to", "with", "for", "of", "in", "on", "at", "is", "are",
  "i", "my", "me", "am", "feel", "feeling", "looking", "want", "need", "some",
  "someone", "people", "that", "this", "help", "just", "really",
])

export function tokenize(prompt: string): string[] {
  if (!prompt?.trim()) return []
  const raw = prompt.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
  return Array.from(new Set(raw.filter((t) => t.length >= 3 && !STOPWORDS.has(t))))
}

type EventLike = {
  id: string
  title?: string | null
  description?: string | null
  additional_info?: string | null
  categories?: { name: string }[]
  subcategories?: { name: string }[]
  hosts?: {
    facilitatorProfile?: {
      professional_title?: string | null
      profile_tags?: string[]
      primary_focus?: string | null
      primary_focus_areas?: string[]
      short_bio?: string | null
    } | null
  }[]
}

// Field weights:
//   profile_tags / subcategory / category               -> 3
//   title / professional_title / primary_focus(_areas)  -> 2
//   description / additional_info / short_bio           -> 1
function haystack(ev: EventLike): { text: string; weight: number }[] {
  const out: { text: string; weight: number }[] = []
  const push = (w: number, ...parts: (string | null | undefined)[]) => {
    for (const p of parts) if (p) out.push({ text: p.toLowerCase(), weight: w })
  }
  for (const h of ev.hosts ?? []) {
    const fp = h.facilitatorProfile
    if (!fp) continue
    for (const t of fp.profile_tags ?? []) push(3, t)
    push(2, fp.professional_title, fp.primary_focus)
    for (const a of fp.primary_focus_areas ?? []) push(2, a)
    push(1, fp.short_bio)
  }
  for (const s of ev.subcategories ?? []) push(3, s.name)
  for (const c of ev.categories ?? []) push(3, c.name)
  push(2, ev.title)
  push(1, ev.description, ev.additional_info)
  return out
}

export function scoreEvent(ev: EventLike, tokens: string[]): number {
  if (tokens.length === 0) return 0
  const hs = haystack(ev)
  let score = 0
  for (const tok of tokens) {
    for (const { text, weight } of hs) {
      if (text.includes(tok)) score += weight
    }
  }
  return score
}

export function scoreEventsByPrompt<E extends EventLike>(
  events: E[],
  prompt: string,
): { event: E; score: number }[] {
  const tokens = tokenize(prompt)
  if (tokens.length === 0) return events.map((event) => ({ event, score: 0 }))
  return events
    .map((event) => ({ event, score: scoreEvent(event, tokens) }))
    .filter((x) => x.score > 0)
}
